import React, { useState, useCallback } from 'react';
import { AImage } from '../../atoms/AImage/index';
import { AVideo } from '../../atoms/AVideo/index';
import { MButtonOverlay } from '../MButtonOverlay/index';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { isBrightcove, hasVideoCredentials } from '../../utils/videoUtils';
import * as Styled from './MVideoThumbnail.styles';
import {
  type MVideoThumbnailProps,
  MVideoThumbnailAriaLabel,
  MVideoThumbnailErrorMessage,
} from './MVideoThumbnail.types';
import type { MButtonOverlayProps } from '../MButtonOverlay/MButtonOverlay.types';

const DEFAULT_BUTTON_OVERLAY: MButtonOverlayProps = {
  iconOnly: true,
  aIcon: { icon: 'icon-play', size: AIconSize.L },
  imageOverlay: true,
};

const COMPONENT_NAME = 'm-web-ui-kit-MVideoThumbnail';
const TEST_ID = 'web-ui-kit-MVideoThumbnail';

interface ErrorWithMessage {
  message: string;
}

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';

const getErrorMessage = (error: unknown): string =>
  isErrorWithMessage(error) ? error.message : MVideoThumbnailErrorMessage.VideoFailed;

export const MVideoThumbnail: React.FC<MVideoThumbnailProps> = ({
  aImage,
  aVideo,
  mButtonOverlay,
  className,
  ...nativeProps
}) => {
  const [hasImageError, setHasImageError] = useState(false);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  const handleImageError = useCallback(() => setHasImageError(true), []);

  const handlePlayerError = useCallback((error?: unknown) => {
    setVideoError(getErrorMessage(error));
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsVideoActive(false);
  }, []);

  const handlePlay = useCallback(() => {
    if (aVideo && hasVideoCredentials(aVideo)) {
      setIsVideoActive(true);
    }
  }, [aVideo]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handlePlay();
    },
    [handlePlay]
  );

  const hasVideoError = videoError !== null;
  const ariaLabel = aImage?.image?.altText ?? aImage?.alt ?? MVideoThumbnailAriaLabel.DefaultPlay;

  return (
    <Styled.Wrapper
      {...nativeProps}
      aria-label={ariaLabel}
      $aspectRatio={aVideo?.aspectRatio}
      $hasError={hasImageError || hasVideoError}
      $noImage={!aImage}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={className}
      onClick={!isVideoActive ? handlePlay : undefined}
    >
      {isVideoActive && aVideo ? (
        <>
          <Styled.VideoContainer>
            <AVideo
              {...aVideo}
              isPlaying={true}
              autoPlay={true}
              loop={false}
              controls={false}
              onEnded={handleVideoEnded}
              aria-label={ariaLabel}
              {...(isBrightcove(aVideo) && {
                onPlayerError: handlePlayerError,
              })}
            />
          </Styled.VideoContainer>

          {hasVideoError && (
            <Styled.ErrorOverlay role="alert" aria-live="assertive" aria-atomic="true">
              <Styled.ErrorText>{videoError}</Styled.ErrorText>
            </Styled.ErrorOverlay>
          )}
        </>
      ) : (
        <>
          {aImage && (
            <Styled.ThumbnailImage>
              <AImage {...aImage} loading="eager" onError={handleImageError} />
            </Styled.ThumbnailImage>
          )}

          <Styled.OverlayContainer>
            <MButtonOverlay
              imageOverlay={true}
              {...(mButtonOverlay ?? DEFAULT_BUTTON_OVERLAY)}
              onClick={handleOverlayClick}
            />
          </Styled.OverlayContainer>

          {aImage && hasImageError && (
            <Styled.ImageErrorOverlay role="alert" aria-atomic="true">
              <Styled.ErrorText>{MVideoThumbnailErrorMessage.ThumbnailFailed}</Styled.ErrorText>
              {ariaLabel && <Styled.ErrorText>{ariaLabel}</Styled.ErrorText>}
            </Styled.ImageErrorOverlay>
          )}
        </>
      )}
    </Styled.Wrapper>
  );
};

export { TEST_ID as M_VIDEO_THUMBNAIL_TEST_ID };
export * from './MVideoThumbnail.types';
